/**
 * BudgetsPage - Premium Budget Management
 * 
 * FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
 * Uses CategoryEnvelopeContext for real-time envelope updates across all pages
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Home, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import BottomNavigation from '@/components/BottomNavigation';
import DashboardHeader from '@/components/DashboardHeader';
import { premiumAPI } from '@/services/api';
import { BUDGET_CATEGORY_TAXONOMY } from '@/constants/budgetCategories';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrencyWithLocale, getCurrencySymbol } from '@/utils/currencyUtils';
// FINLIST SSOT: Import for real-time envelope updates
import { useCategoryEnvelope } from '@/context/CategoryEnvelopeContext';
// CATEGORY CORE SSOT: Use single source of truth for category names
import { getCategoryDisplayName, normalizeToCategoryTaxonomyId } from '@/constants/categoryCore';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';

function useMonthKey(date = new Date()) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

const DEFAULT_CATEGORY_OPTIONS = BUDGET_CATEGORY_TAXONOMY.map((category) => ({
  id: category.id,
  name: category.name
}));

const toDateInputValue = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateInputValue = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfMonthUTC = (date) => {
  if (!(date instanceof Date)) return null;
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
};

const endOfMonthUTC = (date) => {
  if (!(date instanceof Date)) return null;
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
};

const addDaysUTC = (date, days) => {
  if (!(date instanceof Date)) return null;
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days));
};

const getMonthRangeFromKey = (key) => {
  if (!key || typeof key !== 'string') {
    const now = new Date();
    return {
      start: toDateInputValue(startOfMonthUTC(now)),
      end: toDateInputValue(endOfMonthUTC(now))
    };
  }
  const [yearString, monthString] = key.split('-');
  const year = Number(yearString);
  const monthIndex = Number(monthString) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) {
    const now = new Date();
    return {
      start: toDateInputValue(startOfMonthUTC(now)),
      end: toDateInputValue(endOfMonthUTC(now))
    };
  }
  const baseDate = new Date(Date.UTC(year, monthIndex, 1));
  return {
    start: toDateInputValue(startOfMonthUTC(baseDate)),
    end: toDateInputValue(endOfMonthUTC(baseDate))
  };
};

const formatDisplayDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date);
};

const formatDateRangeDescription = (start, end) => {
  const startLabel = formatDisplayDate(start);
  const endLabel = formatDisplayDate(end);
  if (startLabel && endLabel) {
    return `${startLabel} – ${endLabel}`;
  }
  return startLabel || endLabel || 'No period set';
};

const SearchableDropdown = ({
  label,
  placeholder = 'Select an option',
  options = [],
  selectedOption,
  onSelect,
  disabled = false,
  noSelectionLabel,
  allowNoSelection = false
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = useMemo(() => {
    if (!normalizedQuery) return options;
    return options.filter((option) => option.name.toLowerCase().includes(normalizedQuery));
  }, [options, normalizedQuery]);

  const handleSelect = (option) => {
    onSelect?.(option || null);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="relative" ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-10 w-full items-center justify-between rounded-md border px-3 text-left text-sm transition ${
          disabled
            ? 'cursor-not-allowed bg-gray-50 text-gray-400'
            : 'bg-white text-gray-900 hover:border-gray-400'
        }`}
      >
        <span className="truncate">
          {selectedOption ? selectedOption.name : placeholder}
        </span>
        <ChevronDown size={16} className="ml-2 text-gray-400" />
      </button>
      {open && !disabled && (
        <div className="absolute z-20 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              placeholder="Search..."
              autoFocus
            />
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {allowNoSelection && (
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="flex w-full items-center justify-between px-3 py-2 text-sm text-gray-500 hover:bg-gray-50"
              >
                {noSelectionLabel || 'No selection'}
              </button>
            )}
            {filteredOptions.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
            )}
            {filteredOptions.map((option) => (
              <button
                type="button"
                key={option.id}
                onClick={() => handleSelect(option)}
                className={`flex w-full items-start px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                  selectedOption?.id === option.id ? 'bg-gray-50 font-semibold text-gray-900' : 'text-gray-700'
                }`}
              >
                <span>{option.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const normalizeCategories = (list = []) =>
  list
    .filter((cat) => cat && cat.id)
    .map((cat) => ({ id: cat.id, name: cat.name || 'Untitled category' }));

const mergeBudgetCategories = (categoryList, budgetsList) => {
  const map = new Map();
  categoryList.forEach((cat) => {
    map.set(cat.id, cat);
  });

  (budgetsList || []).forEach((budget) => {
    if (budget.categoryId && !map.has(budget.categoryId)) {
      map.set(budget.categoryId, {
        id: budget.categoryId,
        name: 'Custom category'
      });
    }
  });

  return Array.from(map.values());
};

const createInitialFormState = () => ({
  categoryId: '',
  categoryName: '',
  subcategoryId: '',
  subcategoryName: '',
  limit: '',
  plannedAmount: '',
  actualAmount: '',
  isCustom: false,
  customCategoryName: '',
  customSubcategoryName: ''
});

const BudgetsPage = () => {
  const { currency: userCurrency } = useAuth();
  const currency = userCurrency || 'GBP';
  const currencySymbol = getCurrencySymbol(currency);
  const initialMonthKey = useMemo(() => useMonthKey(), []);
  const initialMonthRange = useMemo(() => getMonthRangeFromKey(initialMonthKey), [initialMonthKey]);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  // FINLIST SSOT: Subscribe to real-time envelope updates from context
  const { envelopes: contextEnvelopes, categories: contextCategories, loading: contextLoading, refreshEnvelopes } = useCategoryEnvelope();

  const [month, setMonth] = useState(initialMonthKey);
  const [periodType, setPeriodType] = useState('monthly');
  const [periodStart, setPeriodStart] = useState(initialMonthRange.start);
  const [periodEnd, setPeriodEnd] = useState(initialMonthRange.end);
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(createInitialFormState);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showActualAmountModal, setShowActualAmountModal] = useState(null);
  const [actualAmountInput, setActualAmountInput] = useState('');
  const [savingActual, setSavingActual] = useState(false);
  const [dismissedBudgets, setDismissedBudgets] = useState(new Set());
  const formatAmount = useCallback(
    (value) => formatCurrencyWithLocale(Number(value) || 0, currency),
    [currency]
  );

  const categoryOptions = useMemo(
    () =>
      BUDGET_CATEGORY_TAXONOMY.map((category) => ({
        id: category.id,
        name: category.name,
        subcategories: category.subcategories || []
      })),
    []
  );

  const taxonomyCategoryMap = useMemo(() => {
    const map = {};
    BUDGET_CATEGORY_TAXONOMY.forEach((category) => {
      map[category.id] = {
        ...category,
        subcategoryMap: (category.subcategories || []).reduce((acc, sub) => {
          acc[sub.id] = sub;
          return acc;
        }, {})
      };
    });
    return map;
  }, []);

  const load = useCallback(async (targetMonth) => {
    setLoading(true);
    setError(null);
    try {
      const [budgetsResult, categoriesResult] = await Promise.allSettled([
        premiumAPI.listBudgets(targetMonth),
        premiumAPI.listCategories()
      ]);

      const budgetsData = budgetsResult.status === 'fulfilled'
        ? budgetsResult.value?.budgets || []
        : [];

      if (budgetsResult.status === 'fulfilled') {
        setBudgets(budgetsData);
      } else {
        console.error('Failed to load budgets', budgetsResult.reason);
        setBudgets([]);
        setError('We couldn’t load your budgets right now. Please try again.');
        toast.error('Unable to load budgets. Please try again.');
      }

      if (categoriesResult.status === 'fulfilled') {
        const loaded = categoriesResult.value?.categories || [];
        const normalized = normalizeCategories(loaded);
        const fallback = normalized.length ? normalized : DEFAULT_CATEGORY_OPTIONS;
        setCategories(mergeBudgetCategories(fallback, budgetsData));
      } else {
        console.error('Failed to load categories', categoriesResult.reason);
        setCategories(mergeBudgetCategories(DEFAULT_CATEGORY_OPTIONS, budgetsData));
        toast.error('Unable to load budget categories at the moment.');
      }
    } catch (e) {
      console.error('Failed to load budget data', e);
      setError('We couldn’t load your budgets right now. Please try again.');
      toast.error('Unable to load budgets. Please try again.');
      setCategories((prev) => (prev.length ? prev : DEFAULT_CATEGORY_OPTIONS));
    } finally {
      setLoading(false);
    }
  }, []);

  // FINLIST SSOT: Sync envelope actual amounts from context when they update in real-time
  // This ensures Budget Page reflects transactions from Shopping List, Expense pages instantly
  useEffect(() => {
    if (!contextEnvelopes.length || !budgets.length) return;
    
    // Update budgets with real-time envelope actual amounts
    setBudgets(prevBudgets => {
      let hasChanges = false;
      const updatedBudgets = prevBudgets.map(budget => {
        // Find matching envelope by categoryId
        const matchingEnvelope = contextEnvelopes.find(
          env => env.categoryId === budget.categoryId || env.category === budget.category
        );
        
        if (matchingEnvelope && matchingEnvelope.actual !== budget.actualAmount) {
          hasChanges = true;
          return {
            ...budget,
            actualAmount: matchingEnvelope.actual,
            remaining: (budget.plannedAmount || 0) - (matchingEnvelope.actual || 0)
          };
        }
        return budget;
      });
      
      // Only return new array if there were actual changes
      return hasChanges ? updatedBudgets : prevBudgets;
    });
  }, [contextEnvelopes]); // Re-run when context envelopes update

  // Check for budgets needing actual amounts
  useEffect(() => {
    if (!budgets.length || loading) return;

    const now = new Date();
    const fiveDaysFromNow = new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000);

    // Find budgets that need actual amounts (within 5 days of end date or past)
    const budgetNeedingActual = budgets.find((budget) => {
      // Skip if already dismissed
      if (dismissedBudgets.has(budget.id)) {
        return false;
      }

      if (budget.actualAmount !== null && budget.actualAmount !== undefined) {
        return false; // Already has actual amount
      }

      if (budget.endDate) {
        const endDate = new Date(budget.endDate);
        // Show if end date is within 5 days or has passed (but not more than 30 days ago)
        const daysUntilEnd = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
        return daysUntilEnd <= 5 && daysUntilEnd >= -30;
      }

      return false;
    });

    if (budgetNeedingActual && !showActualAmountModal) {
      setShowActualAmountModal(budgetNeedingActual);
      setActualAmountInput(budgetNeedingActual.plannedAmount?.toString() || '');
    }
  }, [budgets, loading, showActualAmountModal, dismissedBudgets]);

  useEffect(() => {
    load(month);
  }, [load, month]);

  const handlePeriodTypeChange = (nextType) => {
    if (nextType === periodType) return;
    setPeriodType(nextType);
    if (nextType === 'weekly') {
      const base = parseDateInputValue(periodStart) || new Date();
      const startValue = toDateInputValue(base);
      const endValue = toDateInputValue(addDaysUTC(base, 6));
      setPeriodStart(startValue);
      setPeriodEnd(endValue);
    } else {
      const { start, end } = getMonthRangeFromKey(month);
      setPeriodStart(start);
      setPeriodEnd(end);
    }
  };

  const handleStartDateChange = (value) => {
    if (!value) {
      setPeriodStart('');
      return;
    }
    const parsed = parseDateInputValue(value);
    if (!parsed) return;
    if (periodType === 'weekly') {
      setPeriodStart(value);
      setPeriodEnd(toDateInputValue(addDaysUTC(parsed, 6)));
    } else {
      const firstDay = startOfMonthUTC(parsed);
      const lastDay = endOfMonthUTC(parsed);
      const firstValue = toDateInputValue(firstDay);
      const lastValue = toDateInputValue(lastDay);
      setPeriodStart(firstValue);
      setPeriodEnd(lastValue);
      setMonth(useMonthKey(parsed));
    }
  };

  const handleEndDateChange = (value) => {
    if (!value) {
      setPeriodEnd('');
      return;
    }
    const parsed = parseDateInputValue(value);
    if (!parsed) return;
    if (periodType === 'weekly') {
      setPeriodEnd(value);
      setPeriodStart(toDateInputValue(addDaysUTC(parsed, -6)));
    } else {
      const firstDay = startOfMonthUTC(parsed);
      const lastDay = endOfMonthUTC(parsed);
      setPeriodStart(toDateInputValue(firstDay));
      setPeriodEnd(toDateInputValue(lastDay));
      setMonth(useMonthKey(parsed));
    }
  };

  const handleCategorySelect = (option) => {
    if (!option) {
      setForm((prev) => ({
        ...prev,
        categoryId: '',
        categoryName: '',
        subcategoryId: '',
        subcategoryName: ''
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      categoryId: option.id,
      categoryName: option.name,
      subcategoryId: '',
      subcategoryName: '',
      isCustom: false,
      customCategoryName: '',
      customSubcategoryName: ''
    }));
  };

  const handleSubcategorySelect = (option) => {
    setForm((prev) => ({
      ...prev,
      subcategoryId: option?.id || '',
      subcategoryName: option?.name || ''
    }));
  };

  const toggleCustomCategory = () => {
    setForm((prev) => ({
      ...prev,
      isCustom: !prev.isCustom,
      categoryId: '',
      categoryName: '',
      subcategoryId: '',
      subcategoryName: '',
      customCategoryName: '',
      customSubcategoryName: ''
    }));
  };

  const currentCategory = form.categoryId ? taxonomyCategoryMap[form.categoryId] : null;
  const subcategoryOptions = useMemo(() => {
    return currentCategory?.subcategories?.map((sub) => ({ id: sub.id, name: sub.name })) || [];
  }, [currentCategory]);

  const toISODateString = (value) => {
    if (!value) return null;
    const parsed = parseDateInputValue(value);
    return parsed ? parsed.toISOString() : null;
  };

  const buildBudgetName = () => {
    if (form.isCustom) {
      return [
        (form.customCategoryName || '').trim(),
        (form.customSubcategoryName || '').trim()
      ]
        .filter(Boolean)
        .join(' - ');
    }
    return [form.categoryName, form.subcategoryName].filter(Boolean).join(' - ') || 'Budget';
  };

  const getCategoryLabelForBudget = (budget) => {
    if (budget.isCustom && budget.customCategoryName) {
      return [budget.customCategoryName, budget.customSubcategoryName].filter(Boolean).join(' • ');
    }
    
    // CATEGORY CORE SSOT: Try to get canonical name from categoryCore first
    if (budget.categoryId) {
      const canonicalId = normalizeToCategoryTaxonomyId(budget.categoryId);
      const canonicalName = getCategoryDisplayName(canonicalId || budget.categoryId);
      if (canonicalName && canonicalName !== 'Unknown') {
        const subcategoryFromTaxonomy = budget.subcategoryId
          ? taxonomyCategoryMap[budget.categoryId]?.subcategoryMap?.[budget.subcategoryId]?.name
          : null;
        const subcategoryName = budget.subcategoryName || subcategoryFromTaxonomy;
        return [canonicalName, subcategoryName].filter(Boolean).join(' • ');
      }
    }
    
    // Fallback to existing logic
    const taxonomyCategory = budget.categoryId ? taxonomyCategoryMap[budget.categoryId] : null;
    const subcategoryFromTaxonomy = budget.categoryId && budget.subcategoryId
      ? taxonomyCategory?.subcategoryMap?.[budget.subcategoryId]?.name
      : null;
    const fallbackCategory = categories.find((c) => c.id === budget.categoryId);
    const categoryName = budget.categoryName || taxonomyCategory?.name || fallbackCategory?.name;
    const subcategoryName = budget.subcategoryName || subcategoryFromTaxonomy;
    if (!categoryName && !subcategoryName) return 'Uncategorized';
    return [categoryName, subcategoryName].filter(Boolean).join(' • ');
  };

  const getPeriodLabelForBudget = (budget) => {
    if (budget.startDate || budget.endDate) {
      return `${budget.periodType === 'weekly' ? 'Weekly' : 'Monthly'} • ${formatDateRangeDescription(
        budget.startDate,
        budget.endDate
      )}`;
    }
    return `Month: ${budget.month}`;
  };

  const hasCategorySelection = form.isCustom
    ? form.customCategoryName.trim().length > 0
    : Boolean(form.categoryId);
  const limitValue = Number(form.limit);
  const limitValid = Number.isFinite(limitValue) && limitValue > 0;
  const plannedValue = form.plannedAmount ? Number(form.plannedAmount) : null;
  const plannedValid = !form.plannedAmount || (Number.isFinite(plannedValue) && plannedValue > 0);
  const actualValue = form.actualAmount ? Number(form.actualAmount) : null;
  const actualValid = !form.actualAmount || (Number.isFinite(actualValue) && actualValue > 0);
  const datesValid = Boolean(periodStart && periodEnd);
  const isFormValid = hasCategorySelection && limitValid && plannedValid && actualValid && datesValid;
  const limitError = form.limit && !limitValid ? 'Enter a positive amount' : '';
  const plannedError = form.plannedAmount && !plannedValid ? 'Enter a positive amount' : '';
  const actualError = form.actualAmount && !actualValid ? 'Enter a positive amount' : '';

  useEffect(() => {
    if (periodType === 'monthly') {
      const nextRange = getMonthRangeFromKey(month);
      setPeriodStart(nextRange.start);
      setPeriodEnd(nextRange.end);
    }
  }, [month, periodType]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!isFormValid) {
      toast.error('Please complete all required fields');
      return;
    }
    setSaving(true);
    try {
      await premiumAPI.createBudget({
        name: buildBudgetName(),
        categoryId: form.isCustom ? null : form.categoryId || null,
        categoryName: form.isCustom ? null : form.categoryName || null,
        subcategoryId: form.isCustom ? null : form.subcategoryId || null,
        subcategoryName: form.isCustom ? null : form.subcategoryName || null,
        customCategoryName: form.isCustom ? (form.customCategoryName || '').trim() : null,
        customSubcategoryName: form.isCustom ? ((form.customSubcategoryName || '').trim() || null) : null,
        isCustom: form.isCustom,
        limit: Number(form.limit),
        plannedAmount: form.plannedAmount ? Number(form.plannedAmount) : null,
        actualAmount: form.actualAmount ? Number(form.actualAmount) : null,
        month,
        periodType,
        startDate: toISODateString(periodStart),
        endDate: toISODateString(periodEnd)
      });
      setForm(createInitialFormState());
      toast.success('Budget added');
      await load(month);
    } catch (e) {
      console.error('Failed to create budget', e);
      toast.error('Unable to create budget right now.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveActualAmount = async () => {
    if (!showActualAmountModal) return;

    const amount = parseFloat(actualAmountInput);
    if (actualAmountInput === '' || isNaN(amount) || amount < 0) {
      toast.error('Please enter a valid amount (0 or greater)');
      return;
    }

    try {
      setSavingActual(true);
      const response = await premiumAPI.updateBudget(showActualAmountModal.id, { actualAmount: amount });
      toast.success('Actual budget amount saved!');
      
      // Add to dismissed budgets to prevent re-showing
      setDismissedBudgets((prev) => new Set([...prev, showActualAmountModal.id]));
      
      // Close modal
      setShowActualAmountModal(null);
      setActualAmountInput('');
      
      // Refresh budgets
      await load(month);
    } catch (error) {
      console.error('Failed to update budget:', error);
      toast.error('Failed to save actual amount');
    } finally {
      setSavingActual(false);
    }
  };

  const onDelete = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Budget',
      message: 'Are you sure you want to delete this budget? This action cannot be undone.',
      variant: 'danger',
      confirmText: 'Delete'
    });
    if (!confirmed) return;
    try {
      await premiumAPI.deleteBudget(id);
      toast.success('Budget deleted');
      await load(month);
    } catch (e) {
      console.error('Failed to delete budget', e);
      toast.error('Unable to delete budget right now.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <DashboardHeader />
      
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h1 className="text-2xl font-bold">Budgets</h1>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 px-4 py-2 text-sm font-medium text-white shadow-lg transition"
          >
          <Home size={16} />
          Home
        </Link>
        </div>

        {error && (
          <div className="mb-4 flex items-center justify-between rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => load(month)}
            className="font-semibold text-rose-700 underline-offset-4 hover:underline"
          >
            Try again
          </button>
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Viewing month</label>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="h-10 w-full md:w-60 rounded-md border border-gray-300 px-3"
        />
      </div>

      <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-gray-800">Budget period</p>
            <p className="text-xs text-gray-500">Weekly periods automatically span 7 days. Monthly periods align to the full month.</p>
          </div>
          <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-1 text-sm font-medium">
            {['weekly', 'monthly'].map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => handlePeriodTypeChange(type)}
                className={`rounded-full px-3 py-1 capitalize transition ${
                  periodType === type ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
            <input
              type="date"
              value={periodStart || ''}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="h-10 w-full rounded-md border border-gray-300 px-3"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
            <input
              type="date"
              value={periodEnd || ''}
              onChange={(e) => handleEndDateChange(e.target.value)}
              className="h-10 w-full rounded-md border border-gray-300 px-3"
            />
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-500">
          Current range: {periodStart && periodEnd ? formatDateRangeDescription(periodStart, periodEnd) : 'Select dates to continue'}
        </p>
      </div>

      <form onSubmit={onCreate} className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end">
        <div className="flex-1">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">{form.isCustom ? 'Custom category' : 'Category'}</span>
            <button
              type="button"
              onClick={toggleCustomCategory}
              className="text-xs font-semibold text-blue-600 hover:text-blue-500"
            >
              {form.isCustom ? 'Use preset list' : 'Use custom'}
            </button>
          </div>
          {form.isCustom ? (
            <Input
              value={form.customCategoryName}
              onChange={(e) => setForm((prev) => ({ ...prev, customCategoryName: e.target.value }))}
              placeholder="Enter category name"
            />
          ) : (
            <SearchableDropdown
              placeholder="Search categories"
              options={categoryOptions}
              selectedOption={currentCategory ? { id: currentCategory.id, name: currentCategory.name } : null}
              onSelect={handleCategorySelect}
            />
          )}
        </div>
        <div className="flex-1">
          {form.isCustom ? (
            <Input
              label="Custom subcategory (optional)"
              value={form.customSubcategoryName}
              onChange={(e) => setForm((prev) => ({ ...prev, customSubcategoryName: e.target.value }))}
              placeholder="Enter subcategory"
            />
          ) : (
            <SearchableDropdown
              label="Subcategory"
              placeholder={currentCategory ? 'Search subcategories' : 'Select a category first'}
              options={subcategoryOptions}
              selectedOption={
                form.subcategoryId
                  ? {
                      id: form.subcategoryId,
                      name:
                        form.subcategoryName ||
                        taxonomyCategoryMap[form.categoryId]?.subcategoryMap?.[form.subcategoryId]?.name ||
                        ''
                    }
                  : null
              }
              onSelect={handleSubcategorySelect}
              disabled={!currentCategory}
              allowNoSelection
              noSelectionLabel="No subcategory"
            />
          )}
        </div>
        <div className="w-full xl:w-40">
          <Input
            label={`Budget Limit (${currencySymbol})`}
            type="number"
            min="0"
            inputMode="decimal"
            value={form.limit}
            onChange={(e) => setForm((prev) => ({ ...prev, limit: e.target.value }))}
            placeholder="e.g. 300"
            errorMessage={limitError}
          />
          <p className="text-xs text-gray-500 mt-1">Maximum spending cap</p>
        </div>
        <div className="w-full xl:w-40">
          <Input
            label={`Planned Amount (${currencySymbol})`}
            type="number"
            min="0"
            inputMode="decimal"
            value={form.plannedAmount}
            onChange={(e) => setForm((prev) => ({ ...prev, plannedAmount: e.target.value }))}
            placeholder="Optional"
            errorMessage={plannedError}
          />
          <p className="text-xs text-gray-500 mt-1">Estimated budget</p>
        </div>
        <div className="w-full xl:w-40">
          <Input
            label={`Actual Amount (${currencySymbol})`}
            type="number"
            min="0"
            inputMode="decimal"
            value={form.actualAmount}
            onChange={(e) => setForm((prev) => ({ ...prev, actualAmount: e.target.value }))}
            placeholder="Optional"
            errorMessage={actualError}
          />
          <p className="text-xs text-gray-500 mt-1">Real spending amount</p>
        </div>
        <div className="flex items-end">
          <Button type="submit" loading={saving} disabled={saving || !isFormValid}>
            Add
          </Button>
        </div>
      </form>

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="grid gap-2">
          {budgets.length === 0 && (
            <div className="text-gray-500">No budgets for {month}</div>
          )}
          {budgets.map((b) => (
            <div key={b.id} className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-base font-semibold text-gray-900">{b.name}</span>
                  {b.isCustom && (
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">Custom</span>
                  )}
                </div>
                <p className="text-sm text-gray-600">{getCategoryLabelForBudget(b)}</p>
                <p className="text-xs text-gray-500">{getPeriodLabelForBudget(b)}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right text-sm text-gray-600">
                  <p className="text-lg font-semibold text-gray-900">Limit: {formatAmount(b.limit)}</p>
                  {b.plannedAmount !== null && b.plannedAmount !== undefined && (
                    <p className="text-xs text-blue-600">Planned: {formatAmount(b.plannedAmount)}</p>
                  )}
                  {b.actualAmount !== null && b.actualAmount !== undefined && (
                    <p className="text-xs text-green-600">Actual: {formatAmount(b.actualAmount)}</p>
                  )}
                  {(b.plannedAmount !== null && b.plannedAmount !== undefined) && 
                   (b.actualAmount !== null && b.actualAmount !== undefined) && (
                    (() => {
                      const variance = Number(b.actualAmount) - Number(b.plannedAmount);
                      const isOver = variance > 0;
                      return (
                        <p className={`text-xs font-medium ${isOver ? 'text-red-600' : 'text-green-600'}`}>
                          {isOver ? '-' : '+'}{formatAmount(Math.abs(variance))}
                        </p>
                      );
                    })()
                  )}
                  <p className="text-xs text-gray-500 mt-1">{b.month}</p>
                </div>
                <div className="flex items-center gap-2">
                  {(b.actualAmount === null || b.actualAmount === undefined) && b.endDate && (() => {
                    const now = new Date();
                    const endDate = new Date(b.endDate);
                    const daysUntilEnd = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                    
                    if (daysUntilEnd <= 5 && daysUntilEnd >= -30) {
                      return (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowActualAmountModal(b);
                            setActualAmountInput(b.plannedAmount?.toString() || '');
                          }}
                          className="text-xs"
                        >
                          Add Actual
                        </Button>
                      );
                    }
                  })()}
                  <Button variant="destructive" onClick={() => onDelete(b.id)}>Delete</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actual Amount Modal */}
      {showActualAmountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200 relative">
            {/* Close button */}
            <button
              onClick={() => {
                if (showActualAmountModal) {
                  setDismissedBudgets((prev) => new Set([...prev, showActualAmountModal.id]));
                }
                setShowActualAmountModal(null);
                setActualAmountInput('');
              }}
              disabled={savingActual}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="mb-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Budget Period Ending Soon
              </h3>
              <p className="text-sm text-gray-600">
                Please enter the actual amount spent for this budget to keep accurate records.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="font-semibold text-gray-900 mb-1">{showActualAmountModal.name}</p>
              <p className="text-sm text-gray-600 mb-2">
                {getCategoryLabelForBudget(showActualAmountModal)}
              </p>
              <div className="text-sm space-y-1">
                <p className="text-gray-700">
                  <span className="font-medium">Budget Limit:</span> {formatAmount(showActualAmountModal.limit)}
                </p>
                {showActualAmountModal.plannedAmount && (
                  <p className="text-gray-700">
                    <span className="font-medium">Planned:</span> {formatAmount(showActualAmountModal.plannedAmount)}
                  </p>
                )}
                {showActualAmountModal.endDate && (
                  <p className="text-gray-700">
                    <span className="font-medium">End Date:</span>{' '}
                    {format(new Date(showActualAmountModal.endDate), 'MMM dd, yyyy')}
                  </p>
                )}
              </div>
            </div>

            <div className="mb-6">
              <Input
                label={`Actual Amount Spent (${currencySymbol})`}
                type="number"
                min="0"
                step="0.01"
                value={actualAmountInput}
                onChange={(e) => setActualAmountInput(e.target.value)}
                placeholder="Enter actual amount"
                disabled={savingActual}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the total amount you actually spent in this budget period
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleSaveActualAmount}
                loading={savingActual}
                disabled={savingActual}
                className="flex-1"
              >
                Save Amount
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // Add to dismissed budgets to prevent re-showing
                  if (showActualAmountModal) {
                    setDismissedBudgets((prev) => new Set([...prev, showActualAmountModal.id]));
                  }
                  setShowActualAmountModal(null);
                  setActualAmountInput('');
                }}
                disabled={savingActual}
                className="flex-1"
              >
                Later
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog />

      {/* Bottom Navigation */}
      <BottomNavigation />
      </div>
    </div>
  );
};

export default BudgetsPage;
